import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useIsPlatformUser, useTenantId } from '@/auth/useAuth';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle, Skeleton } from '@/components/ui/card';
import { useOverview } from '@/features/ops/hooks';
import type { MetricsOverview } from '@/features/ops/metrics';
import { formatMetric, type MetricFormat } from '@/lib/format';
import { cn } from '@/lib/utils';

type Overview = MetricsOverview;

interface MetricCard {
  key: string;
  labelKey: string;
  value: (o: Overview) => number | null | undefined;
  format: MetricFormat;
  to?: string;
  alert?: (o: Overview) => boolean;
}

/** Live "right now" figures; each links to the screen where the work is done. */
const todayCards: MetricCard[] = [
  { key: 'runsToday', labelKey: 'dashboard.runsToday', value: (o) => o.runsToday, format: 'int', to: '/runs' },
  { key: 'activeRuns', labelKey: 'ops.dashboard.activeRuns', value: (o) => o.activeRuns, format: 'int', to: '/runs?status=running' },
  { key: 'openReviews', labelKey: 'dashboard.openReviews', value: (o) => o.openReviews, format: 'int', to: '/review', alert: (o) => (o.reviewSlaBreached ?? 0) > 0 },
  { key: 'openEscalations', labelKey: 'dashboard.openEscalations', value: (o) => o.openEscalations, format: 'int', to: '/escalations' },
  { key: 'failures', labelKey: 'dashboard.failures', value: (o) => o.failuresToday, format: 'int', to: '/runs?status=failed', alert: (o) => (o.failuresToday ?? 0) > 0 },
];

/** Range KPIs (10 §4 dashboard cards), default range = last 30 days. */
const rangeCards: MetricCard[] = [
  { key: 'completionRate', labelKey: 'dashboard.completionRate', value: (o) => o.completionRate, format: 'pct' },
  { key: 'escalationRate', labelKey: 'dashboard.escalationRate', value: (o) => o.escalationRate, format: 'pct' },
  { key: 'humanTouchRate', labelKey: 'ops.dashboard.humanTouchRate', value: (o) => o.humanTouchRate, format: 'pct' },
  { key: 'deliveryFailures', labelKey: 'dashboard.deliveryFailures', value: (o) => o.deliveryFailures, format: 'int' },
  { key: 'cost', labelKey: 'dashboard.cost', value: (o) => o.costChf, format: 'chf' },
  { key: 'costPerRun', labelKey: 'ops.dashboard.costPerRun', value: (o) => o.costPerCompletedRunChf, format: 'chf' },
  { key: 'timeToComplete', labelKey: 'ops.dashboard.timeToComplete', value: (o) => o.timeToCompleteH, format: 'int' },
  { key: 'slaBreached', labelKey: 'ops.dashboard.slaBreached', value: (o) => o.reviewSlaBreached, format: 'int' },
];

function MetricTile({ card, data, pending, lang }: { card: MetricCard; data: Overview | undefined; pending: boolean; lang: string }) {
  const { t } = useTranslation();
  const alert = data && card.alert?.(data);
  const body = (
    <Card data-testid={`metric-${card.key}`} className={cn(card.to && 'transition-colors hover:bg-muted/40', alert && 'border-destructive/50')}>
      <CardHeader>
        <CardTitle>{t(card.labelKey)}</CardTitle>
      </CardHeader>
      <CardContent>
        {pending ? (
          <Skeleton className="h-7 w-16" />
        ) : (
          <div className={cn('text-2xl font-semibold tabular-nums', alert && 'text-destructive')}>
            {formatMetric(data ? card.value(data) : null, card.format, lang)}
          </div>
        )}
      </CardContent>
    </Card>
  );
  return card.to ? <Link to={card.to}>{body}</Link> : body;
}

/** Runs started per day as a compact bar strip. */
function RunsSeries({ series }: { series: NonNullable<Overview['series']> }) {
  const { t } = useTranslation();
  const max = Math.max(1, ...series.map((p) => p.runsStarted));
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('ops.dashboard.runsPerDay')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex h-24 items-end gap-0.5" role="img" aria-label={t('ops.dashboard.runsPerDay')}>
          {series.map((p) => (
            <div
              key={p.day}
              title={`${p.day}: ${p.runsStarted} · ✓ ${p.completed} · ✗ ${p.failed}`}
              className="flex-1 rounded-t bg-accent/60"
              style={{ height: `${Math.max(2, (p.runsStarted / max) * 100)}%` }}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export function DashboardPage() {
  const { t, i18n } = useTranslation();
  const tenantId = useTenantId();
  const isPlatform = useIsPlatformUser();
  const overview = useOverview();
  const data = overview.data;
  const pending = overview.isPending;
  const empty = overview.isError || (data && [...todayCards, ...rangeCards].every((c) => c.value(data) == null || c.value(data) === 0));

  return (
    <div>
      <PageHeader help="dashboard" title={t('dashboard.title')} description={isPlatform && !tenantId ? t('dashboard.selectTenantHint') : undefined} />
      <div className="flex flex-col gap-6 p-6">
        <section>
          <h2 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">{t('ops.dashboard.today')}</h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
            {todayCards.map((c) => (
              <MetricTile key={c.key} card={c} data={data} pending={pending} lang={i18n.language} />
            ))}
          </div>
        </section>
        <section>
          <h2 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">{t('ops.dashboard.last30')}</h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {rangeCards.map((c) => (
              <MetricTile key={c.key} card={c} data={data} pending={pending} lang={i18n.language} />
            ))}
          </div>
        </section>
        {data?.series && data.series.length > 0 && <RunsSeries series={data.series} />}
        {empty && !pending && (
          <div className="rounded-lg border border-dashed p-6 text-center">
            <div className="text-sm font-medium">{t('dashboard.noData')}</div>
            <div className="mt-1 text-sm text-muted-foreground">{t('dashboard.noDataHint')}</div>
          </div>
        )}
      </div>
    </div>
  );
}
